import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Clock, MapPin, FileText, Users, CheckCircle, Globe, Building, Shield } from 'lucide-react';

const Contact = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
    viewport: { once: true }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <motion.section 
        className="bg-custom-pattern py-16 px-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-inter font-bold md:font-black text-acBlack leading-tight mb-6">
            Let's talk about your case
          </h1>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Send a short summary and we'll reply within 24 to 72 hours. Urgent-deadline cases are triaged the same day where possible.
          </p>
        </div>
      </motion.section>

      {/* How We Work Section */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack text-center mb-12">
            How we work from your first message
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-acBlack" strokeWidth={1} />
              </div>
              <div className="text-2xl font-bold text-yellow-600 mb-4">01</div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Submit your case</h3>
              <p className="text-gray-600">
                Use the form to share a brief description and any deadlines.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-acBlack" strokeWidth={1} />
              </div>
              <div className="text-2xl font-bold text-yellow-600 mb-4">02</div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Initial review</h3>
              <p className="text-gray-600">
                We run a quick screen and reply with next steps and a proposed time for a strategy call.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="w-8 h-8 text-acBlack" strokeWidth={1} />
              </div>
              <div className="text-2xl font-bold text-yellow-600 mb-4">03</div>
              <h3 className="text-xl font-inter font-semibold text-acBlack mb-4">Tailored checklist</h3>
              <p className="text-gray-600">
                You receive a tailored document checklist so we can start quickly.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Contact Details */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack text-center mb-12">Contact details</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              {
                title: "General enquiries",
                email: "info@migpath.com",
                icon: <Mail className="w-6 h-6" strokeWidth={1} />
              },
              {
                title: "Privacy & data requests",
                email: "privacy@migpath.com",
                icon: <Shield className="w-6 h-6" strokeWidth={1} />
              },
              {
                title: "Legal notices",
                email: "legal@migpath.com",
                icon: <FileText className="w-6 h-6" strokeWidth={1} />
              },
              {
                title: "Billing & refunds",
                email: "billing@migpath.com",
                icon: <CheckCircle className="w-6 h-6" strokeWidth={1} />
              },
              {
                title: "Complaints",
                email: "complaints@migpath.com",
                icon: <Users className="w-6 h-6" strokeWidth={1} />
              }
            ].map((contact, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="text-acBlack">{contact.icon}</div>
                </div>
                <h3 className="font-semibold text-acBlack mb-2">{contact.title}</h3>
                <a href={`mailto:${contact.email}`} className="text-acBlack hover:text-gray-700 transition-colors text-sm">
                  {contact.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Offices */}
      <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack text-center mb-12">Offices</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* London Office */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                  <MapPin className="w-8 h-8 text-acBlack" strokeWidth={1} />
                </div>
                <h3 className="text-xl font-inter font-semibold text-acBlack">London - EMEA Office</h3>
              </div>
              <div className="space-y-3 text-gray-700">
                <p>338 Euston Rd., London NW1 3BT, UK</p>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-acBlack" strokeWidth={1} />
                  <span className="text-sm">Mon–Fri, 09:00–18:00 (GMT/BST)</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-acBlack" strokeWidth={1} />
                  <a href="mailto:emea@migpath.com" className="text-acBlack hover:text-gray-700 transition-colors text-sm">
                    emea@migpath.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-acBlack" strokeWidth={1} />
                  <a href="tel:+4407537166096" className="text-acBlack hover:text-gray-700 transition-colors text-sm">
                    +44 (0)75 3716 6096
                  </a>
                </div>
              </div>
            </div>
            
            {/* New York Office */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                  <Globe className="w-8 h-8 text-acBlack" strokeWidth={1} />
                </div>
                <h3 className="text-xl font-inter font-semibold text-acBlack">New York - APAC Desk</h3>
              </div>
              <div className="space-y-3 text-gray-700">
                <p>477 Madison Ave, 6th Floor, New York, NY 10022, United States</p>
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-acBlack" strokeWidth={1} />
                  <span className="text-sm">Mon–Fri, 09:00–18:00 ET (with evening ET overlap for APAC)</span>
                </div>
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-acBlack" strokeWidth={1} />
                  <a href="mailto:apac@migpath.com" className="text-acBlack hover:text-gray-700 transition-colors text-sm">
                    apac@migpath.com
                  </a>
                </div>
                <div className="flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-acBlack" strokeWidth={1} />
                  <a href="tel:+15853618353" className="text-acBlack hover:text-gray-700 transition-colors text-sm">
                    +1 (585) 361-8353
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Response Time */}
      <motion.section className="py-16 px-6 bg-gray-50" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-50 rounded-lg p-8 border border-yellow-200">
            <div className="flex items-start justify-center">
              <CheckCircle className="w-6 h-6 text-acBlack mr-4 mt-1 flex-shrink-0" strokeWidth={1} />
              <div className="text-center">
                <h3 className="text-xl font-semibold text-acBlack mb-3">Response time</h3>
                <p className="text-gray-700">
                  We typically respond within 24–72 hours on business days. Urgent-deadline cases are prioritised and triaged the same day where possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Registered Details */}
      {/* <motion.section className="py-16 px-6" {...fadeInUp}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-inter font-bold text-acBlack text-center mb-12">Registered details (UK)</h2>
          
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
                <Building className="w-8 h-8 text-acBlack" strokeWidth={1} />
              </div>
              <h3 className="text-xl font-inter font-semibold text-acBlack">BATTISON IT SOLUTIONS LIMITED</h3>
            </div>
            <div className="space-y-3 text-gray-700 text-center">
              <p><strong>Trading as:</strong> "Migpath"</p>
              <p><strong>Company number:</strong> 08581421 (England & Wales)</p>
              <p><strong>Registered office:</strong> The Stable Yard, Vicarage Road, Stony Stratford, Milton Keynes, Buckinghamshire, MK11 1BN, United Kingdom</p>
            </div>
          </div>
        </div>
      </motion.section> */}
    </div>
  );
};

export default Contact;
